import { CommonButtonStyles } from '../molecules';
import { Theme } from './types';

const commonButtonStyles = {
    display: 'flex',
    padding: '0.5rem 1.5rem',
    alignItems: 'center',
    gap: '0.5rem',
    borderRadius: '24px',
    border: 'unset',
} as const satisfies CommonButtonStyles;

const secAndTertial = {
    ...commonButtonStyles,
    default: {
        outline: '0.5px solid #007272',
        background: '#FFFFFF',
        color: '#007272',
        border: 'unset',
    },
    hover: {
        outline: '2px solid #14555A',
        background: '#FFFFFF',
        color: '#007272',
    },
    focus: {
        outline: '2px solid #14555A',
        background: 'white',
        color: '#007272',
    },
    active: {
        background: '#14555A',
        color: '#A5E1D2',
        outline: '1px solid #007272',
    },
    disabled: {
        outline: '1px solid #83D5D5',
        background: '#FFFFFF',
        color: '#83D5D5',
    },
};

export const dnbTheme = {
    input: {
        active: { outline: '0.125rem #007272 solid' },
        default: {
            border: 'unset',
            borderRadius: '0.25rem',
            outline: '1px #007272 solid',
            color: '#737373',
            background: '#FFFFFF',
            padding: '0.5rem 1rem',
        },
        hover: { outline: '0.125rem #007272 solid' },
        disabled: {
            outline: '1px #B3D5D5 solid',
        },
        error: {},
        focus: { outline: '0.125rem #14555A solid' },
        placeholder: {},
    },
    toggleButton: {
        selected: {
            ...secAndTertial,
            default: {
                background: `#14555A`,
                outline: '0.125rem #14555A solid',
                color: '#A5E1D2',
            },
            hover: {
                background: '#D2F0E9',
                outline: '0.125rem #14555A solid',
                color: '#14555A',
            },
            focus: {
                background: `#A5E1D2`,
                outline: '0.125rem #14555A solid',
                color: '#14555A',
            },
            active: {
                background: `white`,
                outline: '0.125rem #007272 solid',
            },
            error: { background: `#DC2A2A`, color: `#FFFFFF` },
        },
        unselected: {
            ...secAndTertial,
            active: {
                background: `#14555A`,
                outline: '0.125rem #14555A solid',
                color: '#A5E1D2',
            },
            error: { background: `#DC2A2A`, color: `#FFFFFF` },
        },
    },
    progressDots: {
        color: '#007272',
    },
    button: {
        primary: {
            ...commonButtonStyles,
            default: {
                background: '#007272',
                color: '#FFFFFF',
            },
            hover: {
                outline: '2px solid #14555A',
                background: '#FFFFFF',
                color: '#007272',
            },
            focus: {
                outline: '2px solid #14555A',
                background: '#FFFFFF',
                color: '#007272',
            },
            active: {
                background: '#D2F0E9',
                color: '#007272',
                outline: 'unset',
            },
            disabled: {
                background: '#b3d5d5',
                color: '#FFFFFF',
                outline: 'unset',
            },
        },
        secondary: secAndTertial,
        ghost: {
            ...secAndTertial,
            default: {
                outline: 'unset',
                background: 'transparent',
                color: '#007272',
            },
            active: {
                background: '#D2F0E9',
                color: '#007272',
                outline: 'unset',
            },
        },
        tertiary: secAndTertial,
        danger: {
            ...commonButtonStyles,
            default: {
                outline: '1px solid #0033FF',
                background: 'transparent',
            },
            hover: {
                outline: '1px solid #0033FF',
                background: 'rgba(0, 51, 255, 0.1)',
            },
            focus: {
                outline: '4px solid #0033FF',
            },
            active: {
                outline: '1px solid #0033FF',
                background: '#C7DBFF',
            },
            disabled: {
                outline: '1px solid #E0E0E0',
                background: '#F4F8FF',
                color: '#A0A0A0',
            },
        },
    },
    checkbox: {
        unselected: {
            active: {
                background: '#D2F0E9',
                outline: `0.09375rem solid #D2F0E9`,
            },
            default: {
                width: '20px',
                height: '20px',
                background: 'white',
                outline: `0.09375rem solid #007272`,
            },
            svg: {
                stroke: 'transparent',
            },
            hover: {
                outline: `0.125rem solid #007272`,
                background: '#D2F0E9',
                border: 'unset',
            },
            focus: {
                border: 'unset',
                outline: `0.125rem solid #14555A`,
                background: '#D2F0E9',
            },
            disabled: {
                outline: `0.125rem solid #D2F0E9`,
            },
            error: {
                outline: `0.125rem solid #DC2A2A`,
                background: 'white',
                hover: {
                    outline: `0.125rem solid #DC2A2A`,
                    background: '#FDEEEE',
                },
                focus: {
                    outline: `0.125rem solid #14555A`,
                    background: '#FDEEEE',
                },
            },
        },
        selected: {
            active: {
                background: '#D2F0E9',
                outline: `0.09375rem solid #D2F0E9`,
                svg: {
                    stroke: '#ffff',
                },
            },
            default: {
                width: '20px',
                height: '20px',
                background: '#007272',
                outline: `0.125rem solid #007272`,
            },
            svg: {
                stroke: '#ffff',
            },
            hover: {
                background: `#D2F0E9`,
                outline: `0.125rem solid #D2F0E9`,
                svg: {
                    stroke: '#007272',
                },
            },
            focus: {
                outline: `2px solid #14555A`,
                background: `#D2F0E9`,
                svg: {
                    stroke: '#14555A',
                },
            },
            disabled: {
                background: '#B3D5D5',
                svg: {
                    stroke: 'white',
                },
            },
            error: {
                outline: `0.125rem solid #DC2A2A`,
                background: 'white',
                svg: {
                    stroke: '#DC2A2A',
                },
                hover: {
                    outline: `0.125rem solid #DC2A2A`,
                    background: '#FDEEEE',
                },
                focus: {
                    outline: `0.125rem solid #14555A`,
                    background: '#FDEEEE',
                },
            },
        },
    },
    texts: {
        links: {
            default: {
                color: '#007272',
                border: 'unset',
            },
            visited: {
                default: {
                    color: '#007272',
                },
                active: {
                    color: '#A5E1D2',
                    background: '#14555A',
                    borderRadius: '0.25rem',
                    border: 'unset',
                    textDecoration: 'underline',
                },
                hover: {
                    color: '#007272',
                    background: '#D2F0E9',
                    borderRadius: '0.25rem',
                    border: 'unset',
                    textDecoration: 'none',
                },
                focus: {
                    outline: '1.5px solid #14555A',
                    textDecoration: 'none',
                },
            },
            active: {
                color: '#A5E1D2',
                borderRadius: '0.25rem',
                background: '#14555A',
            },
            hover: {
                color: '#007272',
                borderRadius: '0.25rem',
                background: '#D2F0E9',
                border: 'unset',
                textDecoration: 'none',
            },
            focus: {
                borderRadius: '0.25rem',
                outline: '1.5px solid #14555A',
                textDecoration: 'none',
            },
        },
        error: {
            default: {
                color: '#DC2A2A',
            },
        },
        default: {
            background: 'white',
        },
        typography: {
            defaults: {
                color: '#333333',
                weights: {
                    medium: {
                        fontWeight: 500,
                    },
                    bold: {
                        fontWeight: 700,
                    },
                    regular: {
                        fontWeight: 400,
                    },
                },
            },

            button: {
                fontSize: '1.125rem',
                lineHeight: '1.5rem',

                fontWeight: 400,
                color: '#007272',
            },
            label: {
                fontSize: '1.125rem',
                lineHeight: '1.5rem',

                fontWeight: 400,
            },
            body: {
                mobile: {
                    fontSize: '1.125rem',
                    lineHeight: '1.375rem',
                },

                fontSize: '1.125rem',
                lineHeight: '1.375rem',
            },
            body2: {
                fontWeight: 400,
                lineHeight: '1.25rem',
                fontSize: '1rem',

                mobile: {
                    lineHeight: '1.25rem',
                    fontSize: '1rem',
                },
            },
            h3: {
                fontSize: '2.125rem',
                fontWeight: 500,
                lineHeight: '2.5rem',
                mobile: {
                    fontSize: '2.125rem',
                    lineHeight: '2.5rem',
                },
            },
            h4: {
                fontSize: '1.625rem',
                lineHeight: '2rem',
                fontWeight: 500,
                mobile: {
                    fontSize: '1.625rem',
                    lineHeight: '2rem',
                },
            },
        },
    },
} satisfies Theme;
